import React, { useEffect, useRef, useState } from "react";
import type { UserLocation } from "../../../map/layers/useUserLocationsLayer";
import { useMapSettings } from "../../context/useMapSettings";
import { useAuth } from "../../../../context/user";
import { apiV2 } from "../../../../api/axiosConfig";

type SearchUser = {
    _id: string;
    clientID: string;
    clientName: string;
    email: string;
    firstName: string;
    lastName: string;
    receivedAt: string;
};

type SearchDevice = {
    id: string;
    name: string;
    clientName?: string;
};

type TrackMode = "user" | "device";

type Props = {
    userList: UserLocation[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export function TrackingMenu({ userList, open, onOpenChange }: Props) {
    const { settings, setTrackingUserID, setTrackingDeviceID, setTrackingDays } = useMapSettings();
    const { trackingUserID, trackingUserName, trackingDeviceID, trackingDays, clientID } = settings;
    const { isSuperAdmin } = useAuth();

    const [mode, setMode] = useState<TrackMode>("user");
    const [search, setSearch] = useState("");
    const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
    const [searching, setSearching] = useState(false);
    const [deviceSearch, setDeviceSearch] = useState("");
    const [deviceResults, setDeviceResults] = useState<SearchDevice[]>([]);
    const [deviceSearching, setDeviceSearching] = useState(false);
    const [trackedDeviceName, setTrackedDeviceName] = useState("");
    const [draftDays, setDraftDays] = useState(trackingDays);
    const containerRef = useRef<HTMLDivElement>(null);

    const isTracking = !!(trackingUserID || trackingDeviceID);
    const activeTrackingName = trackingUserID ? trackingUserName : trackedDeviceName;

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                onOpenChange(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open, onOpenChange]);

    // Clear search and results when closed
    useEffect(() => {
        if (!open) { setSearch(""); setSearchResults([]); setDeviceSearch(""); setDeviceResults([]); }
    }, [open]);

    // Sync draft when trackingDays changes externally (e.g. reset)
    useEffect(() => { setDraftDays(trackingDays); }, [trackingDays]);

    // Debounce draft → committed value
    useEffect(() => {
        const t = setTimeout(() => setTrackingDays(draftDays), 500);
        return () => clearTimeout(t);
    }, [draftDays]); // eslint-disable-line

    // Debounced user search
    useEffect(() => {
        const q = search.trim();
        if (!q) { setSearchResults([]); return; }
        setSearching(true);
        const params: Record<string, string> = { q };
        if (isSuperAdmin && clientID) params.clientID = clientID;
        const t = setTimeout(() => {
            apiV2.get<SearchUser[]>("ops/user-search", { params })
                .then((r) => setSearchResults(r.data ?? []))
                .catch(() => setSearchResults([]))
                .finally(() => setSearching(false));
        }, 300);
        return () => { clearTimeout(t); setSearching(false); };
    }, [search, clientID, isSuperAdmin]); // eslint-disable-line

    // Debounced device search
    useEffect(() => {
        const q = deviceSearch.trim();
        if (!q) { setDeviceResults([]); return; }
        setDeviceSearching(true);
        const params: Record<string, string> = { q };
        if (clientID) params.clientID = clientID;
        const t = setTimeout(() => {
            apiV2.get<SearchDevice[]>("ops/device-search", { params })
                .then((r) => setDeviceResults(r.data ?? []))
                .catch(() => setDeviceResults([]))
                .finally(() => setDeviceSearching(false));
        }, 300);
        return () => { clearTimeout(t); setDeviceSearching(false); };
    }, [deviceSearch, clientID]); // eslint-disable-line

    function stopTracking() {
        setTrackingUserID(null);
        setTrackingDeviceID(null);
        setTrackedDeviceName("");
        onOpenChange(false);
    }

    return (
        <div ref={containerRef} className="relative">
            <button
                onClick={() => onOpenChange(!open)}
                title="Track"
                className={`flex items-center gap-1.5 px-3 h-12 rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.18)] text-sm font-medium transition-colors border-none cursor-pointer ${
                    isTracking
                        ? "bg-global-purple text-white hover:brightness-110"
                        : "bg-white text-grey hover:bg-ice"
                }`}
            >
                <span className="material-icons text-[18px] leading-none">my_location</span>
                {isTracking ? (
                    <>
                        <span className="w-1.5 h-1.5 rounded-full bg-white/70 animate-pulse flex-shrink-0" />
                        <span className="max-w-[180px] truncate">
                            {activeTrackingName || "Tracking"}
                        </span>
                    </>
                ) : (
                    <span>Track</span>
                )}
            </button>

            {open && (
                <div className="absolute top-full mt-1 left-0 w-80 bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.18)] overflow-hidden z-50">
                    <div className="p-4 flex flex-col gap-3">
                        {isTracking ? (
                            <>
                                {/* Active tracking state */}
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-global-purple animate-pulse flex-shrink-0" />
                                    <span className="text-[13px] font-semibold text-dark-dark-grey flex-1 truncate">
                                        {activeTrackingName || "Unknown"}
                                    </span>
                                    <button
                                        onClick={stopTracking}
                                        className="text-[12px] text-light-grey bg-transparent border-none cursor-pointer p-0 hover:text-red flex-shrink-0"
                                    >
                                        Stop tracking
                                    </button>
                                </div>

                                <hr className="border-none border-t border-light-light-grey m-0" />

                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">History depth</p>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="range"
                                            min={1}
                                            max={30}
                                            step={1}
                                            value={draftDays}
                                            onChange={(e) => setDraftDays(Number(e.target.value))}
                                            className="flex-1 accent-global-purple"
                                        />
                                        <span className="text-[12px] text-dark-dark-grey w-14 text-right flex-shrink-0">
                                            {draftDays === 1 ? "1 day" : `${draftDays} days`}
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-light-grey mt-1 mb-0">Location history shown on map</p>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Mode switch */}
                                <div className="flex rounded-lg overflow-hidden border border-light-light-grey">
                                    <button
                                        onClick={() => setMode("user")}
                                        className={`flex-1 text-[12px] font-medium py-1.5 border-none cursor-pointer transition-colors ${
                                            mode === "user" ? "bg-global-purple text-white" : "bg-white text-grey hover:bg-ice"
                                        }`}
                                    >
                                        User
                                    </button>
                                    <button
                                        onClick={() => setMode("device")}
                                        className={`flex-1 text-[12px] font-medium py-1.5 border-none cursor-pointer transition-colors ${
                                            mode === "device" ? "bg-global-purple text-white" : "bg-white text-grey hover:bg-ice"
                                        }`}
                                    >
                                        Device
                                    </button>
                                </div>

                                {mode === "user" ? (
                                    <>
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-0 mt-0">Select user to track</p>
                                        <input
                                            type="text"
                                            placeholder="Search by name or email…"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            autoComplete="off"
                                            className="w-full text-[13px] px-2 py-1.5 border border-light-light-grey rounded-md outline-none text-dark-dark-grey box-border"
                                        />
                                        {search.trim() === "" ? (
                                            <p className="text-[13px] text-light-grey m-0">Type a name or email to search</p>
                                        ) : searching ? (
                                            <p className="text-[13px] text-light-grey m-0">Searching…</p>
                                        ) : searchResults.length === 0 ? (
                                            <p className="text-[13px] text-light-grey m-0">No users found</p>
                                        ) : (
                                            <div className="flex flex-col gap-0.5 max-h-48 overflow-y-auto">
                                                {searchResults.map((u) => (
                                                    <button
                                                        key={u._id}
                                                        onClick={() => {
                                                            const name = `${u.firstName} ${u.lastName}`.trim();
                                                            const days = u.receivedAt
                                                                ? Math.min(30, Math.max(1, Math.ceil((Date.now() - new Date(u.receivedAt).getTime()) / 86_400_000)))
                                                                : undefined;
                                                            setTrackingUserID(u._id, name, days);
                                                            setSearch("");
                                                            onOpenChange(false);
                                                        }}
                                                        className="flex flex-col items-start px-2 py-2 rounded-md bg-transparent border-none cursor-pointer hover:bg-ice text-left"
                                                    >
                                                        <span className="text-[13px] font-medium text-dark-dark-grey">
                                                            {u.firstName} {u.lastName}
                                                        </span>
                                                        <span className="text-[11px] text-grey">{u.email}</span>
                                                        {isSuperAdmin && <span className="text-[11px] text-light-grey">{u.clientName}</span>}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-0 mt-0">Select device to track</p>
                                        <input
                                            type="text"
                                            placeholder="Search by device name…"
                                            value={deviceSearch}
                                            onChange={(e) => setDeviceSearch(e.target.value)}
                                            autoComplete="off"
                                            className="w-full text-[13px] px-2 py-1.5 border border-light-light-grey rounded-md outline-none text-dark-dark-grey box-border"
                                        />
                                        {deviceSearch.trim() === "" ? (
                                            <p className="text-[13px] text-light-grey m-0">Type a device name to search</p>
                                        ) : deviceSearching ? (
                                            <p className="text-[13px] text-light-grey m-0">Searching…</p>
                                        ) : deviceResults.length === 0 ? (
                                            <p className="text-[13px] text-light-grey m-0">No devices found</p>
                                        ) : (
                                            <div className="flex flex-col gap-0.5 max-h-48 overflow-y-auto">
                                                {deviceResults.map((d) => (
                                                    <button
                                                        key={d.id}
                                                        onClick={() => {
                                                            setTrackingDeviceID(d.id);
                                                            setTrackedDeviceName(d.name);
                                                            setDeviceSearch("");
                                                            onOpenChange(false);
                                                        }}
                                                        className="flex flex-col items-start px-2 py-2 rounded-md bg-transparent border-none cursor-pointer hover:bg-ice text-left"
                                                    >
                                                        <span className="text-[13px] font-medium text-dark-dark-grey">{d.name}</span>
                                                        {isSuperAdmin && d.clientName && <span className="text-[11px] text-light-grey">{d.clientName}</span>}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
